/**
 * SubmissionStatsCards Component
 *
 * Renders clickable stats cards showing:
 * - Total submissions
 * - With individual report
 * - With referidos report
 * - Without report
 * - Selected count
 *
 * Cards act as filters when clicked
 *
 * Follows Single Responsibility Principle - only handles stats display
 */

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { SubmissionStats, ReportStatusFilter } from "../store/types";

interface SubmissionStatsCardsProps {
  /** Calculated statistics */
  stats: SubmissionStats;
  /** Currently active report status filter */
  activeFilter: ReportStatusFilter;
  /** Callback when a stat card is clicked to filter */
  onFilterClick: (filter: ReportStatusFilter) => void;
  /** Whether selection is active (compresses cards) */
  hasSelection: boolean;
  /** Total count from server (database) */
  serverTotal?: number;
}

/**
 * Stats cards component with filter functionality
 *
 * @example
 * ```tsx
 * <SubmissionStatsCards
 *   stats={stats}
 *   activeFilter={reportStatusFilter}
 *   onFilterClick={setReportStatus}
 *   hasSelection={selectedIds.size > 0}
 * />
 * ```
 */
export function SubmissionStatsCards({
  stats,
  activeFilter,
  onFilterClick,
  hasSelection,
  serverTotal,
}: SubmissionStatsCardsProps) {
  // Use server total if available, otherwise fall back to loaded total
  const displayTotal = serverTotal ?? stats.total;
  return (
    <div
      className={cn(
        "grid gap-3 transition-all duration-300",
        hasSelection
          ? "w-1/2 grid-cols-2 md:grid-cols-4"
          : "w-full grid-cols-2 md:grid-cols-5"
      )}
    >
      {/* Total */}
      <Card
        className={cn(
          "cursor-pointer transition-all hover:shadow-md",
          activeFilter === "all" && "ring-2 ring-primary"
        )}
        onClick={() => onFilterClick("all")}
      >
        <CardContent
          className={cn(
            "transition-all duration-300",
            hasSelection ? "p-3" : "p-4"
          )}
        >
          <p className="text-xs text-muted-foreground">Total</p>
          <p
            className={cn(
              "font-bold transition-all duration-300",
              hasSelection ? "text-xl" : "text-2xl"
            )}
          >
            {displayTotal}
          </p>
        </CardContent>
      </Card>

      {/* Con reporte - Grouped */}
      <Card
        className={cn(
          "overflow-hidden",
          hasSelection ? "col-span-1 md:col-span-2" : "md:col-span-2"
        )}
      >
        <div className="px-2 py-1 bg-muted/50 border-b">
          <p className="text-[10px] font-medium text-muted-foreground">
            Con reporte
          </p>
        </div>
        <div className="grid grid-cols-2 divide-x">
          {/* Individual */}
          <CardContent
            className={cn(
              "cursor-pointer transition-all hover:bg-muted/30",
              hasSelection ? "p-2" : "p-4",
              activeFilter === "with_individual" &&
                "bg-green-50 ring-2 ring-inset ring-green-500"
            )}
            onClick={() =>
              onFilterClick(
                activeFilter === "with_individual" ? "all" : "with_individual"
              )
            }
          >
            <p className="text-[10px] text-muted-foreground">Individual</p>
            <p
              className={cn(
                "font-bold text-green-600 transition-all duration-300",
                hasSelection ? "text-lg" : "text-xl"
              )}
            >
              {stats.withIndividual}
            </p>
          </CardContent>

          {/* Referidos */}
          <CardContent
            className={cn(
              "cursor-pointer transition-all hover:bg-muted/30",
              hasSelection ? "p-2" : "p-4",
              activeFilter === "with_referidos" &&
                "bg-green-50 ring-2 ring-inset ring-green-500"
            )}
            onClick={() =>
              onFilterClick(
                activeFilter === "with_referidos" ? "all" : "with_referidos"
              )
            }
          >
            <p className="text-[10px] text-muted-foreground">Referidos</p>
            <p
              className={cn(
                "font-bold text-green-600 transition-all duration-300",
                hasSelection ? "text-lg" : "text-xl"
              )}
            >
              {stats.withReferidos}
            </p>
          </CardContent>
        </div>
      </Card>

      {/* Sin reporte */}
      <Card
        className={cn(
          "cursor-pointer transition-all hover:shadow-md",
          activeFilter === "without_report" && "ring-2 ring-orange-500"
        )}
        onClick={() =>
          onFilterClick(
            activeFilter === "without_report" ? "all" : "without_report"
          )
        }
      >
        <CardContent
          className={cn(
            "transition-all duration-300",
            hasSelection ? "p-3" : "p-4"
          )}
        >
          <p className="text-xs text-muted-foreground">Sin reporte</p>
          <p
            className={cn(
              "font-bold text-orange-500 transition-all duration-300",
              hasSelection ? "text-xl" : "text-2xl"
            )}
          >
            {stats.withoutReport}
          </p>
        </CardContent>
      </Card>

      {/* Seleccionados - Solo visible cuando NO hay selección */}
      {!hasSelection && (
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Seleccionados</p>
            <p className="text-2xl font-bold text-blue-600">{stats.selected}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
